import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { YshBadgeComponent } from '../ysh-badge/ysh-badge.component';
import { GalleryImage, YshGalleryComponent } from '../ysh-gallery/ysh-gallery.component';
import { Inspection, InspectionField, InspectionSection } from '../../models/inspection';

// Scalar answers that map to a colored status badge. Anything else renders as
// a plain value (or an image / chips, depending on shape).
const STATUS_VALUES = ['ok', 'pass', 'fail', 'needs repair', 'complete', 'pending'];

/**
 * Renders the structured, label-resolved inspection report returned by the
 * order detail endpoint: sections → groups → fields. Field rendering is driven
 * off the (already display-ready) value shape rather than `fieldType` so it
 * degrades gracefully.
 *
 * The Pass/Fail result and date live in the page's Overview panel, so they are
 * intentionally omitted here. For the legacy/slim shapes the caller should fall
 * back to its existing rendering — `inspection.isStructured` guards this
 * component.
 */
@Component({
    selector: 'ysh-inspection-report',
    templateUrl: './ysh-inspection-report.component.html',
    styleUrl: './ysh-inspection-report.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [YshBadgeComponent, YshGalleryComponent]
})
export class YshInspectionReportComponent {
  @Input() report: Inspection;

  get sections(): InspectionSection[] {
    return this.report?.sections ?? [];
  }

  /** Photo fields are detected by the presence of attached photos. */
  isPhoto(field: InspectionField): boolean {
    return (field.photos?.length ?? 0) > 0;
  }

  /** Multi-select values arrive as an array of labels. */
  isMultiSelect(field: InspectionField): boolean {
    return Array.isArray(field.value);
  }

  /** Data-URL values (e.g. captured signatures) render as an image. */
  isDataUrl(field: InspectionField): boolean {
    return typeof field.value === 'string' && field.value.startsWith('data:');
  }

  /** Pass/fail-style answers render as a colored badge. */
  isStatus(field: InspectionField): boolean {
    return typeof field.value === 'string'
      && STATUS_VALUES.includes(field.value.trim().toLowerCase());
  }

  /** Labels for a multi-select field, as strings. */
  asList(field: InspectionField): string[] {
    return Array.isArray(field.value) ? field.value.map(value => String(value)) : [];
  }

  /** Field photos mapped to gallery thumbnails (caption = photo label). */
  fieldPhotos(field: InspectionField): GalleryImage[] {
    return (field.photos ?? []).map(photo => ({
      src: photo.url,
      caption: photo.label,
    }));
  }

  /** Display string for scalar (text/number/select/status) fields. */
  displayValue(field: InspectionField): string {
    if (field.value == null || field.value === '') return '—';
    return String(field.value);
  }
}
